// @ts-nocheck

import { useRef, useEffect, useCallback, useState, useLayoutEffect } from 'react';
import type { ElementRef } from 'react';

import { useViewportContext } from '../contexts/ViewportContext';
import { getAppDOMElement } from '../DOMElements';
import { getDimensions } from '../lib/core/dom/dimensions';

const MARGIN = 10;
const MAX_WIDTH_RATIO = 50 / 100;
const MIN_WIDTH_RATIO = 20 / 100;
const DEFAULT_WIDTH_RATIO = 23 / 100;
const FIRST_POSITION_MARGIN = 10;

const clamp = (x, y, width, height, viewportWidth, viewportHeight) => {
  const newX = Math.max(0, Math.min(x, viewportWidth - width));
  const newY = Math.max(0, Math.min(y, viewportHeight - height));
  return { x: newX, y: newY };
};

const clampVideoWidth = (width, viewportWidth) =>
  Math.max(Math.min(width, viewportWidth * MAX_WIDTH_RATIO), viewportWidth * MIN_WIDTH_RATIO);

/**
 * ONLY FOR DESKTOP
 * - creates all the react-gesture mecanisms (drag and drop, resize)
 * - add custom cursors when we approach the video edges
 *
 * @param {ElementRef<'div'>} domElem the target element where the gesture is applied
 * @param {boolean} isActive activate or not the gesture
 */
const useMouseDragAndResize = (domElem: ElementRef<'div'>, isActive: boolean = true) => {
  const { width: viewportWidth, height: viewportHeight } = useViewportContext();
  const [isDragging, setIsDragging] = useState(false);
  const isClickOnEdge = useRef(false);
  const isClickToDrag = useRef(false);
  const initialMousePosition = useRef({ x: 0, y: 0 });
  const initialVideoPosition = useRef({ x: 0, y: 0, width: 0, height: 0, ratio: 0 });
  const isEdges = useRef({
    isLeft: false,
    isRight: false,
    isTop: false,
    isBottom: false,
  });

  const minRelWidth = viewportWidth * MIN_WIDTH_RATIO;
  const maxRelWidth = viewportWidth * MAX_WIDTH_RATIO;

  const onMove = useCallback(
    (e: MouseEvent) => {
      if (!domElem) {
        return;
      }

      const { x: initialMouseX, y: initialMouseY } = initialMousePosition.current;
      const { x: initialVideoLeft, y: initialVideoTop } = initialVideoPosition.current;
      const appDOMElem = getAppDOMElement();
      if (isClickToDrag.current) {
        // drag
        appDOMElem.style.userSelect = 'none';
        const posX = initialVideoLeft + (e.clientX - initialMouseX);
        const posY = initialVideoTop + (e.clientY - initialMouseY);
        const { width, height } = getDimensions(domElem);
        const { x: finalX, y: finalY } = clamp(posX, posY, width, height, viewportWidth, viewportHeight);
        domElem.style.transform = `translate3d(${finalX}px, ${finalY}px, 0)`;
      } else if (!isClickOnEdge.current) {
        const { left, top, width, height } = getDimensions(domElem);
        const x = e.clientX - left;
        const y = e.clientY - top;
        const isTop = y < MARGIN;
        const isLeft = x < MARGIN;
        const isRight = x >= width - MARGIN;
        const isBottom = y >= height - MARGIN;

        isEdges.current = {
          isTop,
          isLeft,
          isRight,
          isBottom,
        };

        // style cursor
        if ((isRight && isBottom) || (isLeft && isTop)) {
          domElem.style.cursor = 'nwse-resize';
        } else if ((isRight && isTop) || (isBottom && isLeft)) {
          domElem.style.cursor = 'nesw-resize';
        } else if (isRight || isLeft) {
          domElem.style.cursor = 'ew-resize';
        } else if (isBottom || isTop) {
          domElem.style.cursor = 'ns-resize';
        } else {
          domElem.style.cursor = 'grab';
        }
      } else {
        // resize
        appDOMElem.style.userSelect = 'none';
        const { width: initialVideoWidth, height: initialVideoHeight, ratio } = initialVideoPosition.current;
        const { isTop, isLeft, isRight, isBottom } = isEdges.current;
        let newWidth = null;
        let posX = null;
        let posY = null;

        if (isRight) {
          const mouseDiff = e.clientX - initialMouseX;
          newWidth = clampVideoWidth(mouseDiff + initialVideoWidth, viewportWidth);
        }
        if (isBottom) {
          const mouseDiff = e.clientY - initialMouseY;
          const currentHeight = mouseDiff + initialVideoHeight;
          newWidth = clampVideoWidth((currentHeight / initialVideoHeight) * initialVideoWidth, viewportWidth);
        }
        if (isLeft) {
          const mouseDiff = initialMouseX - e.clientX;
          newWidth = clampVideoWidth(mouseDiff + initialVideoWidth, viewportWidth);
          const posXmax = initialVideoLeft + (initialVideoWidth - minRelWidth);
          const posXmin = initialVideoLeft - (maxRelWidth - initialVideoWidth);
          posX = Math.max(posXmin, Math.min(initialVideoLeft - mouseDiff, posXmax));
          posY = initialVideoTop;
        }
        if (isTop) {
          const mouseDiffY = initialMouseY - e.clientY;
          const currentHeight = mouseDiffY + initialVideoHeight;
          newWidth = clampVideoWidth((currentHeight / initialVideoHeight) * initialVideoWidth, viewportWidth);
          const maxHeight = maxRelWidth * ratio;
          const minHeight = minRelWidth * ratio;

          posY = initialVideoTop - mouseDiffY;
          const posYmax = initialVideoTop + (initialVideoHeight - minHeight);
          const posYmin = initialVideoTop - (maxHeight - initialVideoHeight);
          posY = Math.max(posYmin, Math.min(posY, posYmax));
          const videoWidthDiff = initialVideoWidth - newWidth;
          posX = initialVideoLeft + (isLeft ? videoWidthDiff : 0);
          const posXmax = initialVideoLeft + (initialVideoWidth - minRelWidth);
          const posXmin = initialVideoLeft - (maxRelWidth - initialVideoWidth);
          posX = Math.max(posXmin, Math.min(posX, posXmax));
        }

        if (newWidth !== null) {
          domElem.style.width = `${newWidth}px`;
          if (posX !== null && posY !== null) {
            const newHeight = (newWidth / initialVideoWidth) * initialVideoHeight;
            const { x: finalX, y: finalY } = clamp(posX, posY, newWidth, newHeight, viewportWidth, viewportHeight);
            domElem.style.transform = `translate3d(${finalX}px, ${finalY}px, 0)`;
          }
        }
      }
    },
    [domElem, viewportWidth, viewportHeight, minRelWidth, maxRelWidth],
  );

  const onUp = useCallback(() => {
    getAppDOMElement().style.removeProperty('user-select');
    isClickOnEdge.current = false;
    isClickToDrag.current = false;
    setIsDragging(false);
  }, []);

  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!domElem) {
        return;
      }
      const { isTop, isLeft, isRight, isBottom } = isEdges.current;
      const isMouseOnEdge = isTop || isLeft || isRight || isBottom;
      if (isMouseOnEdge) {
        isClickOnEdge.current = true;
      } else {
        isClickToDrag.current = true;
      }
      setIsDragging(true);
      const { left, top, width, height } = getDimensions(domElem);
      initialMousePosition.current = { x: e.clientX, y: e.clientY };
      initialVideoPosition.current = { x: left, y: top, width, height, ratio: height / width };
    },
    [domElem],
  );

  // place video when entering floating pip mode
  useLayoutEffect(() => {
    if (isActive && domElem) {
      const { width: initWidth, height: initHeight } = getDimensions(domElem);
      const ratio = initHeight / initWidth;
      const relWidth = viewportWidth * DEFAULT_WIDTH_RATIO;
      const width = Math.max(Math.min(relWidth, maxRelWidth), minRelWidth);
      const height = width * ratio;
      const x = viewportWidth - width - FIRST_POSITION_MARGIN;
      const y = viewportHeight - height - FIRST_POSITION_MARGIN;

      domElem.style.width = `${width}px`;
      domElem.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    }
  }, [isActive, viewportWidth, viewportHeight, domElem, minRelWidth, maxRelWidth]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    if (domElem) {
      domElem.addEventListener('mousedown', onMouseDown);
    }

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (domElem) {
        domElem.removeEventListener('mousedown', onMouseDown);
      }
    };
  }, [onMove, onUp, onMouseDown, domElem, isActive]);

  return { isDragging };
};

export default useMouseDragAndResize;
